import React, {
  Fragment,
  HTMLAttributes,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import { pluralize } from 'humanize-plus'
import { isEmpty } from 'ramda'

import { Button, InlineInput, InlineSelect, Modal } from '@driftt/dds'
import { Driftiwyg } from '@driftt/tide-driftiwyg'

import { useEmailPersonalizationTokens } from 'hooks/usePersonalizationTokens'
import {
  GuestNotification,
  GuestNotifications,
  GuestNotificationTypeEnum,
} from 'types/MeetingType'
import {
  createDelayTimeFromNormalized,
  createDisplayTimeFromMs,
  determineDelayUnit,
  HOUR,
  MINUTE,
} from 'utils/TimeHelpers'

export interface IEditEmailTemplateModalProps {
  onClose: () => void
  onSave: (updatedGuestNotifications: GuestNotifications) => void
  readOnly?: boolean
  isOrgMeeting: boolean
  type: GuestNotificationTypeEnum
  guestNotifications: GuestNotifications
}

export const EmailTemplateModal = ({
  onClose,
  onSave,
  readOnly,
  isOrgMeeting,
  type,
  guestNotifications,
}: IEditEmailTemplateModalProps) => {
  const copyPhrase = isOrgMeeting ? 'your teammate.' : 'you.'

  const modalProps = useMemo(
    () => ({
      [GuestNotificationTypeEnum.MEETING_CONFIRMATION]: {
        title: 'Personalize confirmation',
        copy: `after a guest books a meeting with ${copyPhrase}`,
      },
      [GuestNotificationTypeEnum.MEETING_REMINDER]: {
        title: 'Personalize reminder',
        copy: `before a guest’s meeting with  ${copyPhrase}`,
      },
      [GuestNotificationTypeEnum.MEETING_REMINDER_FOLLOW]: {
        title: 'Personalize reminder',
        copy: `before a guest’s meeting with  ${copyPhrase}`,
      },
    }),
    [copyPhrase]
  )

  const currentEmail: GuestNotification = guestNotifications[type]

  const { allTokens } = useEmailPersonalizationTokens()
  const emailSubject = useRef(currentEmail.emailSubject)
  const emailBody = useRef(currentEmail.emailBody)
  const [emailDelayTime, setEmailDelayTime] = useState(
    currentEmail.delayTime || Number(HOUR)
  )
  const [emailDelayUnit, setEmailDelayUnit] = useState(
    determineDelayUnit(currentEmail.delayTime) || 'hour'
  )

  const validSubject = useRef(!!emailSubject && emailSubject.current !== '')
  const validBody = useRef(!!emailBody && emailBody.current !== '')

  useEffect(() => {
    emailSubject.current = currentEmail.emailSubject
    emailBody.current = currentEmail.emailBody
    setEmailDelayTime(currentEmail.delayTime)
    setEmailDelayUnit(determineDelayUnit(currentEmail.delayTime) || 'hour')
  }, [currentEmail])

  const setDelayTime = (event: any) => {
    const value = Number(event.target.value)
    const newDelayValue = createDelayTimeFromNormalized(value, emailDelayUnit)
    setEmailDelayTime(newDelayValue)
  }
  const setDelayUnit = (value: any) => {
    setEmailDelayUnit(value)
    if (value === 'minute' && emailDelayTime > MINUTE * 90) {
      setEmailDelayTime(MINUTE * 90)
    }
  }
  const onSubjectUpdate = (subjectText: string) => {
    emailSubject.current = subjectText
    validSubject.current = !!subjectText && subjectText !== ''
  }
  const onBodyUpdate = (bodyText: string) => {
    emailBody.current = bodyText
    validBody.current = !!bodyText && bodyText !== ''
  }

  const displayTime = createDisplayTimeFromMs(emailDelayTime, emailDelayUnit)
  const pluralizedTimeUnit =
    emailDelayUnit === 'minute'
      ? pluralize(displayTime, 'minute')
      : pluralize(displayTime, 'hour')
  const delayUnitOptions = [
    {
      value: 'hour',
      label: pluralize(displayTime, 'Hour'),
    },
    {
      value: 'minute',
      label: pluralize(displayTime, 'Minute'),
    },
  ]

  const canSave = validBody && validSubject

  const handleSave = (
    delayTime: number,
    emailSubject: string,
    emailBody: string
  ) =>
    onSave({
      ...guestNotifications,
      [type]: {
        delayTime,
        emailSubject,
        emailBody,
        on: true,
      },
    })

  return (
    <Modal.Root open onClose={onClose}>
      <Modal.Dialog size="large" variant="info">
        <Modal.Title title={modalProps[type].title} />
        <Modal.Content>
          <div>
            {'This email will be sent '}
            {readOnly ? (
              <>
                {displayTime} {pluralizedTimeUnit}
              </>
            ) : (
              <>
                <InlineInput
                  variant="input"
                  rootProps={
                    {
                      min: '0',
                      max: `${emailDelayUnit === 'hour' ? 24 : 90}`,
                    } as HTMLAttributes<HTMLInputElement>
                  }
                  type="number"
                  value={displayTime}
                  onChange={setDelayTime}
                  label={'Time delay'}
                />
                <InlineSelect
                  options={delayUnitOptions}
                  value={emailDelayUnit}
                  onValueChange={setDelayUnit}
                  label={'delay time unit'}
                />
              </>
            )}
            {` ${modalProps[type].copy}`}
          </div>
          {readOnly ? (
            <div>
              <div>
                <div
                  dangerouslySetInnerHTML={{
                    __html: emailSubject.current,
                  }}
                />
              </div>
              <div>
                <div
                  dangerouslySetInnerHTML={{
                    __html: emailBody.current,
                  }}
                />
              </div>
            </div>
          ) : (
            <div>
              {!!allTokens && !isEmpty(currentEmail) && (
                <Fragment>
                  <div>
                    <Driftiwyg
                      className="assistant-emails--subject-editor-driftiwyg"
                      placeholder="Add a subject line"
                      editorState={emailSubject.current}
                      onChange={onSubjectUpdate}
                      companyTagList={allTokens.company}
                      meetingTagList={allTokens.meeting}
                      userTagList={allTokens.user}
                      toolbarOptions={['emoji', 'tag']}
                      v2
                    />
                    <Driftiwyg
                      className="assistant-emails--body-editor-driftiwyg"
                      placeholder="Add your email body"
                      editorState={emailBody.current}
                      onChange={onBodyUpdate}
                      companyTagList={allTokens.company}
                      meetingTagList={allTokens.meeting}
                      userTagList={allTokens.user}
                      toolbarOptions={[
                        'bold',
                        'italic',
                        'underline',
                        'link',
                        'emoji',
                        'image',
                        'tag',
                      ]}
                      v2
                    />
                  </div>
                </Fragment>
              )}
            </div>
          )}
        </Modal.Content>
        <Modal.Actions>
          {readOnly ? (
            <Button variant="primary" onClick={onClose}>
              Done
            </Button>
          ) : (
            <>
              <Button
                variant="primary"
                onClick={() =>
                  handleSave(
                    emailDelayTime,
                    emailSubject.current,
                    emailBody.current
                  )
                }
                disabled={!canSave}
              >
                Save
              </Button>
              <Button variant="tertiary" onClick={onClose}>
                Cancel
              </Button>
            </>
          )}
        </Modal.Actions>
      </Modal.Dialog>
    </Modal.Root>
  )
}

export default EmailTemplateModal
